import ImportPollingStatusError from './polling-status-error'

/**
 * A helper class to store information about the current polling state to
 * import students. We need this information to decide whether or not we want to continue executing
 * the polling request.
 *
 * @class PollingStatus
 */
export default class PollingStatus {
  /**
   * Constructor defines the valid states and checks if the passed in status. If an invalid status
   * is passed in, we will throw and ImportPollingStatusError which will be caught by our error handling
   * and reported to log. The polling will be interrupted and shows an error message to the user. Its is important
   * for the status codes to be correct as we are making workflow decisions on them.
   *
   * @param {string | null} status
   * @param {string} jobId
   */
  constructor(status, jobId) {
    this.states = {
      scheduled: 'scheduled',
      busy: 'busy',
      complete: 'complete',
      retry: 'retry',
      failed: 'failed',
    }

    if (this._isInvalidStatus(status)) throw new ImportPollingStatusError(status)

    this.status = status
    this.jobId = jobId
  }

  /**
   * Alternante constructor
   * @param status
   * @param jobId
   * @returns {PollingStatus}
   */
  static create(status, jobId) {
    return new PollingStatus(status, jobId)
  }

  /**
   * Check if an unknown status was passed in from the backend. We also also allow null as a valid value
   * as it would indicate that a polling request has failed due to the absence of the status property
   *
   * @param {String|null} status
   * @returns {boolean}
   * @private
   */
  _isInvalidStatus(status) {
    if (status === null) return false
    return Object.keys(this.states).indexOf(status) < 0
  }

  /**
   * @returns {boolean}
   */
  isComplete() {
    return this.status === this.states.complete
  }

  /**
   * Either null or a failed are valid states for a failed polling request. We also taking into account
   * @returns {boolean}
   */
  hasFailed() {
    return this.status === this.states.failed || this.status === null
  }

  /**
   * Busy, scheduled and retry indicate if polling is still required
   * @returns {boolean}
   */
  isStillRunning() {
    const { states } = this
    return this.status === states.scheduled || this.status === states.busy || this.status === states.retry
  }
}
